import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Beaker, CalendarDays, CheckCircle2, ClipboardList, Download, FileText,
  Loader2, Pencil, RefreshCw, Search, Trash2, UserCog, Users, X, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import api, { formatApiError } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { StaffSmsPausePanel } from "@/components/StaffSmsPausePanel";
import { RescheduleNoticeHoldPanel } from "@/components/RescheduleNoticeHoldPanel";
import { AvailabilityHealthPanel } from "@/components/AvailabilityHealthPanel";
import { ReportReviewPanel } from "@/components/tank-health-check/ReportReviewPanel";
import { CustomerActionsPanel } from "@/components/tank-health-check/CustomerActionsPanel";

/**
 * Phase C — operational view of the Tank Water Health Check campaign.
 *
 * Everything shown here is read straight from the stored booking (no
 * derived copies): lab test added or not, collection state, technician,
 * communications and the cancel/reschedule audit. `sample_collection_status`
 * is the only collection truth on screen.
 */
const PAGE = 20;
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLLECTION_TONE = {
  to_collect: "bg-rose-100 text-rose-700 border-rose-200",
  collected: "bg-emerald-100 text-emerald-700 border-emerald-200",
  suspended: "bg-amber-100 text-amber-700 border-amber-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

const AUTO_SEND_LABEL = {
  scheduled: "Auto-send scheduled",
  processing: "Auto-send in progress",
  sent: "Auto-sent",
  failed: "Auto-send failed",
  cancelled_manual_send: "Sent manually",
  cancelled_admin: "Auto-send cancelled",
  cancelled_booking: "Auto-send cancelled (booking)",
  superseded: "Superseded by a newer version",
  auto_send_disabled: "Awaiting manual send",
};

const AUTO_SEND_TONE = {
  scheduled: "bg-amber-100 text-amber-700 border-amber-200",
  processing: "bg-amber-100 text-amber-700 border-amber-200",
  sent: "bg-emerald-100 text-emerald-700 border-emerald-200",
  failed: "bg-rose-100 text-rose-700 border-rose-200",
};

// Feb 2026 — "cancelled_manual_send" only means a manual send was CLAIMED;
// it is written whether that attempt went on to succeed, fail, get stuck
// "sending" forever, or land "uncertain" (AWHC-T9YERA, AWHC-407BAC).
// Anywhere this status is shown without a sent_at, fall back to the real
// email_status instead of the generic "Sent manually" label so a stuck,
// failed or uncertain send is never shown as delivered.
const manualSendLabel = (emailStatus, expired) =>
  emailStatus === "sending" ? "Manual send in progress"
    : emailStatus === "uncertain"
      ? (expired ? "Send outcome unknown – reconciliation required"
        : "Send outcome unknown – review before retry")
      : emailStatus === "failed" ? "Manual send failed – retry"
        : "Manual send pending";
const manualSendTone = (emailStatus) =>
  emailStatus === "sending" ? "bg-amber-100 text-amber-700 border-amber-200"
    : emailStatus === "uncertain" ? "bg-orange-100 text-orange-700 border-orange-200"
      : "bg-rose-100 text-rose-700 border-rose-200";
// Resend's Idempotency-Key only guarantees a retry is deduped for 24h
// (see backend IDEMPOTENCY_SAFE_RETRY_HOURS = 23h, matching margin). Past
// that, Send Now is blocked server-side (409) until a human reconciles.
const isUncertainExpired = (emailStatus, uncertainAt) =>
  emailStatus === "uncertain" && uncertainAt
  && (Date.now() - new Date(uncertainAt).getTime()) > 23 * 3600 * 1000;

const Pill = ({ children, tone = "bg-slate-100 text-slate-600 border-slate-200", testid }) => (
  <span data-testid={testid}
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${tone}`}>
    {children}
  </span>
);

// Older flag rows stored raw ServiceM8 UUIDs where a name couldn't be read;
// keep them readable instead of showing a 36-character id.
const short = (v) => (v && v.length > 12 ? `${v.slice(0, 8)}…` : v);
const staffLabel = (x) => {
  const who = x.staff_name && x.staff_name !== "unknown"
    ? x.staff_name : `unnamed staff ${short(x.staff_uuid)}`;
  const by = x.created_by && x.created_by !== "unknown"
    ? x.created_by : "unknown";
  return `${who} (added by ${by})`;
};

// Oct 2026 — every timestamp below is stored in UTC, but the business and
// every customer/technician are in Queensland, which never observes DST, so
// a fixed Australia/Brisbane conversion is always exactly AEST.
const fmtAEST = (iso) => {
  if (!iso) return "—";
  try {
    return `${new Date(iso).toLocaleString("en-AU", {
      timeZone: "Australia/Brisbane", day: "numeric", month: "short",
      year: "numeric", hour: "2-digit", minute: "2-digit",
    })} AEST`;
  } catch { return String(iso); }
};
const fmtAESTDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      timeZone: "Australia/Brisbane", day: "numeric", month: "short", year: "numeric",
    });
  } catch { return String(iso); }
};


export default function TankWaterHealthChecks() {
  const [rows, setRows] = useState(null);
  const [techs, setTechs] = useState([]);
  const [queue, setQueue] = useState(null);
  const [cleanup, setCleanup] = useState(null);
  const [editor, setEditor] = useState(null);
  const [access, setAccess] = useState(null);
  const [techReview, setTechReview] = useState(null);
  const [moveTo, setMoveTo] = useState({});
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("all");
  const [scope, setScope] = useState("upcoming");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const [detail, setDetail] = useState(null);
  const [fu, setFu] = useState(null);
  const [fuType, setFuType] = useState("");
  const [fuStatus, setFuStatus] = useState("");
  const [autoSend, setAutoSend] = useState(null);
  // Jun 2026 — out-of-region referral counters (shared Leads console).
  const [referrals, setReferrals] = useState(null);
  const [delayDraft, setDelayDraft] = useState("");
  const [sms, setSms] = useState(null);
  const [smsDraft, setSmsDraft] = useState({});

  const loadSms = useCallback(async () => {
    const { data } = await api.get("/admin/tank-water-health-check/internal-notifications");
    setSms(data);
    setSmsDraft(Object.fromEntries(data.recipients.map((r) => [r.key, r.mobile || ""])));
  }, []);

  const saveSms = async (patch) => {
    try {
      const { data } = await api.put("/admin/tank-water-health-check/internal-notifications", patch);
      setSms(data);
      setSmsDraft(Object.fromEntries(data.recipients.map((r) => [r.key, r.mobile || ""])));
      toast.success("Internal SMS alerts saved");
    } catch (e) { toast.error(e.response?.data?.detail || formatApiError(e)); }
  };

  const saveSmsRecipients = () => saveSms({
    recipients: (sms?.recipients || []).map((r) => ({
      key: r.key, name: r.name, mobile: smsDraft[r.key] ?? r.mobile, enabled: r.enabled,
    })),
  });

  const loadAutoSend = useCallback(async () => {
    const { data } = await api.get("/admin/tank-water-health-check/report-auto-send");
    setAutoSend(data);
    setDelayDraft(String(data.delay_minutes));
  }, []);

  const saveAutoSend = async (patch) => {
    try {
      const { data } = await api.put("/admin/tank-water-health-check/report-auto-send", patch);
      setAutoSend(data);
      setDelayDraft(String(data.delay_minutes));
      toast.success("Report auto-send settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || formatApiError(e)); }
  };

  const loadFollowUps = useCallback(async (t = fuType, s = fuStatus) => {
    const q = new URLSearchParams({ include_test: "true" });
    if (t) q.set("action_type", t);
    if (s) q.set("status", s);
    const { data } = await api.get(`/admin/tank-water-health-check/follow-ups?${q}`);
    setFu(data);
  }, [fuType, fuStatus]);

  const openDetail = async (ref, force = false) => {
    if (!force && detail?.header?.reference === ref) { setDetail(null); return; }
    try {
      const { data } = await api.get(`/admin/tank-water-health-check/bookings/${ref}/health-check`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/tank-water-health-check/bookings?include_test=true");
    setRows(data.bookings || []);
    api.get("/admin/tank-water-health-check/referral-summary")
      .then(({ data: r }) => setReferrals(r)).catch(() => {});
    api.get("/field/technicians").then(({ data: t }) => setTechs(t.technicians || [])).catch(() => {});
    api.get("/admin/tank-water-health-check/alternate-collection-queue")
      .then(({ data: q }) => setQueue(q)).catch(() => {});
    api.get("/tank-water-health-check/slots").then(({ data: s }) => setSlots(s.slots || [])).catch(() => {});
  }, []);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [load]);
  useEffect(() => { loadFollowUps().catch(() => {}); }, [loadFollowUps]);
  useEffect(() => { loadAutoSend().catch(() => {}); }, [loadAutoSend]);
  useEffect(() => { loadSms().catch(() => {}); }, [loadSms]);

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || formatApiError(e)); }
    finally { setBusy(""); }
  };

  // Jun 2026 — duplicate ServiceM8 technician blocks. The stored warning can
  // outlive the duplicate, so the office needs BOTH "fix it" and "is it
  // actually still broken?" without having to move the job.
  const cleanupSm8 = (b) => act(async () => {
    const { data } = await api.post(
      `/admin/integrations/servicem8/appointments/${b.appointment_id}/cleanup-staff`);
    const n = data.pruned.length;
    if (data.message) {
      toast.info(data.message, { duration: 10000 });
    } else if (!n && !data.remaining_extras.length) {
      toast.info("Nothing to remove — ServiceM8 already has one technician.");
    } else if (data.remaining_extras.length) {
      toast.warning(`Removed ${n}, but ${data.remaining_extras.length} still on the job.`);
    }
  }, "ServiceM8 checked");

  // Jun 2026 — recovery: put AWARE's date/time/tech back on the ServiceM8 job
  // if the job has ended up with no scheduling at all.
  const repushSm8 = (b) => act(async () => {
    const { data } = await api.post(
      `/admin/integrations/servicem8/appointments/${b.appointment_id}/repush-schedule`);
    toast.info(`ServiceM8 now shows ${data.blocks.length} scheduled block(s).`,
      { duration: 8000 });
  }, "Schedule pushed back to ServiceM8");

  const recheckSm8 = (b) => act(async () => {
    const { data } = await api.post(
      `/admin/integrations/servicem8/appointments/${b.appointment_id}/recheck-staff`);
    if (data.extras.length) {
      toast.warning(`Still ${data.extras.length} extra: ${data.extras.map(staffLabel).join(", ")}`);
    } else if (data.was_stale) {
      toast.success("Already sorted in ServiceM8 — stale warning cleared.");
    }
  }, "Checked against ServiceM8");
  // Sep 2026 — ServiceM8 assigned the job to a staff member with no AWARE
  // technician record. Two honest outcomes: AWARE's tech is right (dismiss),
  // or that ServiceM8 person needs linking to an AWARE technician for good.
  const openTechReview = async (b) => {
    try {
      const { data } = await api.get(
        `/admin/tank-water-health-check/bookings/${b.reference}/technician-review`);
      setTechReview({ ...data, technician_id: "" });
    } catch (e) { toast.error(e.response?.data?.detail || formatApiError(e)); }
  };

  const dismissTechReview = (b) => act(async () => {
    await api.post(
      `/admin/tank-water-health-check/bookings/${b.reference}/technician-review/dismiss`);
    setTechReview(null);
  }, `Flag cleared — ${b.assigned_technician_name || "the AWARE technician"} stands`);

  const mapTechReview = (b) => act(async () => {
    if (!techReview?.technician_id) throw new Error("Pick an AWARE technician first");
    await api.post(
      `/admin/tank-water-health-check/bookings/${b.reference}/technician-review/map`,
      { technician_id: techReview.technician_id });
    setTechReview(null);
  }, "ServiceM8 staff member linked — they won't flag again");



  // Jun 2026 — the office edits the report BEFORE it goes out. Sent versions
  // are never altered; they are reissued.
  const openEditor = async (ref, version) => {
    try {
      const { data } = await api.get(
        `/admin/tank-water-health-check/bookings/${ref}/reports/${version}/edit`);
      setEditor({ ...data, reference: ref });
    } catch (e) { toast.error(e.response?.data?.detail || formatApiError(e)); }
  };

  const saveEditor = async (ref, reviewStatus) => {
    try {
      const { data } = await api.put(
        `/admin/tank-water-health-check/bookings/${ref}/reports/${editor.version}`,
        {
          overall_summary: editor.overall_summary,
          technician_summary: editor.technician_summary,
          owner_notes: editor.owner_notes,
          recommendations: (editor.recommendations || []).map((r) => r.trim()).filter(Boolean),
          photo_ids: (editor.photos || []).filter((p) => p.included).map((p) => p.id),
          // Jun 2026 — Recommendation Review: wording, priority, order, approval.
          customer_facing_assessment: editor.customer_facing_assessment || "",
          recommendation_updates: Object.fromEntries(
            (editor.recommendation_cards || []).map((c) => [c.recommendation_type, {
              reason: c.reason, scope: c.scope, priority: c.priority,
              recommended_test_package: c.recommended_test_package,
            }])),
          recommendation_order: (editor.recommendation_cards || [])
            .map((c) => c.recommendation_type),
          ...(reviewStatus ? { review_status: reviewStatus } : {}),
        });
      setEditor({ ...data, reference: ref });
      toast.success(reviewStatus === "approved"
        ? "Approved — the report can now go to the customer"
        : "Report updated — the PDF now shows your changes");
      await openDetail(ref, true);
    } catch (e) { toast.error(e.response?.data?.detail || formatApiError(e)); }
  };

  const moveBooking = (b) => act(async () => {
    const v = moveTo[b.reference] || {};
    if (!v.date || !v.time) { throw new Error("Pick a date and a time first"); }
    await api.post(
      `/admin/tank-water-health-check/bookings/${b.reference}/reschedule-manual`,
      { date: v.date, time: v.time });
    setMoveTo({ ...moveTo, [b.reference]: {} });
  }, "Moved — ServiceM8 and the customer have been updated");

  const checkAccess = async (b) => {
    try {
      const { data } = await api.get(
        `/admin/tank-water-health-check/bookings/${b.reference}/access-check`);
      setAccess({ ...data, reference: b.reference });
      if (data.can_open) toast.success(`${data.technician_name} can open this link`);
    } catch (e) { toast.error(e.response?.data?.detail || formatApiError(e)); }
    finally { setBusy(""); }
  };

  const scanSm8 = () => act(async () => {
    const { data } = await api.post("/admin/integrations/servicem8/recheck-staff-all");
    const bits = [`${data.checked} upcoming visit(s) checked`, `${data.clean} clean`];
    if (data.stale_warnings_cleared) bits.push(`${data.stale_warnings_cleared} stale warning(s) cleared`);
    if (data.unreadable) bits.push(`${data.unreadable} unreadable`);
    if (data.flagged.length) {
      toast.warning(`${data.flagged.length} still double-booked: ${
        data.flagged.map((f) => f.reference).join(", ")}`, { duration: 12000 });
    }
    toast.info(bits.join(" · "), { duration: 10000 });
  }, "ServiceM8 scan complete");

  const stats = useMemo(() => {
    const r = rows || [];
    return {
      total: r.length,
      withTest: r.filter((b) => b.lab_offer_status === "ordered" && !b.lab_order_cancelled_at).length,
      toCollect: r.filter((b) => b.sample_collection_status === "to_collect").length,
      collected: r.filter((b) => b.sample_collection_status === "collected").length,
      cancelled: r.filter((b) => b.status === "cancelled").length,
    };
  }, [rows]);

  const visible = useMemo(() => {
    const r = rows || [];
    const priority = (b) => (b.health_check_summary?.recommendations || [])
      .filter((x) => x !== "No immediate action required").length > 0;
    let out = r;
    if (filter === "incomplete") out = r.filter((b) => b.field_form_status !== "completed" && b.status !== "cancelled");
    else if (filter === "completed") out = r.filter((b) => b.field_form_status === "completed");
    else if (filter === "follow_up") out = r.filter(priority);
    else if (filter === "lab") out = r.filter((b) => b.lab_offer_status === "ordered" && !b.lab_order_cancelled_at);
    else if (filter === "to_collect") out = r.filter((b) => b.sample_collection_status === "to_collect");
    else if (filter === "collected") out = r.filter((b) => b.sample_collection_status === "collected");
    else if (filter === "no_action") out = r.filter((b) =>
      (b.health_check_summary?.recommendations || []).includes("No immediate action required"));
    // Date scope — the queue defaults to what is still coming up.
    const today = new Date().toISOString().slice(0, 10);
    if (scope === "upcoming") out = out.filter((b) => (b.appointment_date || "") >= today);
    else if (scope === "past") out = out.filter((b) => (b.appointment_date || "") < today);
    else if (scope === "week") {
      const end = new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
      out = out.filter((b) => (b.appointment_date || "") >= today && b.appointment_date <= end);
    }
    const term = q.trim().toLowerCase();
    if (term) {
      out = out.filter((b) => [b.first_name, b.last_name, b.reference, b.email,
        b.mobile, b.property_address, b.suburb, b.postcode, b.assigned_technician_name,
        b.lab_order_reference]
        .filter(Boolean).join(" ").toLowerCase().includes(term));
    }
    const dir = scope === "past" ? -1 : 1;
    return [...out].sort((a, b) =>
      ((a.appointment_date || "") > (b.appointment_date || "") ? dir : -dir));
  }, [rows, filter, scope, q]);

  const shown = useMemo(() => visible.slice(0, limit), [visible, limit]);
  useEffect(() => { setLimit(PAGE); }, [filter, scope, q]);

  if (!rows) return (
    <PortalShell>
      <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
    </PortalShell>
  );

  return (
    <PortalShell>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-thc-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Tank Water Health Checks</h1>
            <p className="text-sm text-slate-500 mt-1">
              Onsite Health Check and sample collection by Pristine Water Systems ·
              laboratory testing and reporting by AWARE Water Testing.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-thc-scan-sm8" onClick={() => { setBusy("scan"); scanSm8(); }}
              disabled={busy === "scan"}
              className="text-[12px] font-bold text-slate-700 border border-slate-200 rounded-full px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-50">
              <Users className="w-3.5 h-3.5 inline mr-1" />
              {busy === "scan" ? "Checking ServiceM8…" : "Check ServiceM8 duplicates"}
            </button>
            <button data-testid="admin-thc-refresh" onClick={() => load()}
              className="text-[12px] font-bold text-cyan-700 border border-cyan-200 rounded-full px-3 py-1.5 bg-white hover:bg-cyan-50">
              <RefreshCw className="w-3.5 h-3.5 inline mr-1" />Refresh
            </button>
            <a data-testid="admin-thc-csv" href={`${api.defaults.baseURL}/admin/tank-water-health-check/bookings.csv`}
              className="text-[12px] font-bold text-slate-700 border border-slate-200 rounded-full px-3 py-1.5 bg-white hover:bg-slate-50">
              <Download className="w-3.5 h-3.5 inline mr-1" />CSV
            </a>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-thc-stats">
          {[["Health Checks", stats.total, "admin-thc-stat-total"],
            ["Lab test added", stats.withTest, "admin-thc-stat-with-test"],
            ["Sample to collect", stats.toCollect, "admin-thc-stat-to-collect"],
            ["Sample collected", stats.collected, "admin-thc-stat-collected"],
            ["Cancelled", stats.cancelled, "admin-thc-stat-cancelled"]].map(([label, n, tid]) => (
            <div key={tid} data-testid={tid} className="rounded-xl bg-white border border-slate-200 p-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
              <div className="text-2xl font-bold text-slate-900">{n}</div>
            </div>
          ))}
        </div>

        {/* Jun 2026 — out-of-region enquiries live in the shared Leads
            console; this tile is the doorway, never a second screen. */}
        {referrals && (
          <a href="/admin/quote-leads?source=tank_water_health_check_referral"
            data-testid="admin-thc-referral-tile"
            className="mt-3 block rounded-xl border border-blue-200 bg-blue-50/60 p-4 hover:bg-blue-50 transition-colors">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-blue-900/60">
                  Partner Referrals (outside direct service region)
                </div>
                <div className="text-2xl font-bold text-blue-950" data-testid="admin-thc-referral-total">
                  {referrals.total}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
                <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
                  data-testid="admin-thc-referral-assigned">
                  {referrals.assigned} assigned
                </span>
                <span className="px-2 py-1 rounded-full bg-cyan-100 text-cyan-800 border border-cyan-200"
                  data-testid="admin-thc-referral-awaiting">
                  {referrals.awaiting_contact} awaiting contact
                </span>
                <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-200"
                  data-testid="admin-thc-referral-review">
                  {referrals.needs_admin_review} need admin review
                </span>
              </div>
            </div>
          </a>
        )}

        {autoSend && (
          <div className="mt-5 rounded-xl border border-cyan-200 bg-cyan-50/60 p-4"
            data-testid="admin-thc-auto-send-panel">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[12px] font-extrabold uppercase tracking-wide text-cyan-800">
                  Health Check report auto-send
                </div>
                <p className="mt-1 text-[12px] text-slate-600 max-w-2xl">
                  Completed Health Check reports are emailed to the customer automatically
                  after the delay below, which gives you a window to preview the PDF, send it
                  now, or cancel the scheduled send. This is separate from the Property
                  Campaign auto-send setting.
                </p>
                <div className="mt-2 text-[12px] font-bold text-slate-700"
                  data-testid="admin-thc-auto-send-effective">
                  Currently: {autoSend.enabled
                    ? `ON · reports send ${autoSend.delay_minutes === 0
                      ? "as soon as the report is final"
                      : `${autoSend.delay_minutes} minute${autoSend.delay_minutes === 1 ? "" : "s"} after completion`}`
                    : "OFF · every report waits for a manual Send Now"}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid="admin-thc-auto-send-toggle"
                  onClick={() => saveAutoSend({ enabled: !autoSend.enabled })}
                  className={`rounded-full px-3 py-1.5 text-[12px] font-bold border ${autoSend.enabled
                    ? "bg-cyan-700 text-white border-cyan-700" : "bg-white text-slate-600 border-slate-300"}`}>
                  Auto-send {autoSend.enabled ? "ON" : "OFF"}
                </button>
                <label className="text-[12px] font-bold text-slate-600">
                  Delay
                  <input data-testid="admin-thc-auto-send-delay" type="number" min="0" max="1440"
                    value={delayDraft} onChange={(e) => setDelayDraft(e.target.value)}
                    className="ml-2 w-20 rounded-lg border border-slate-300 px-2 py-1 text-[12px]" />
                  <span className="ml-1 font-normal text-slate-500">min</span>
                </label>
                <button data-testid="admin-thc-auto-send-save"
                  onClick={() => saveAutoSend({ delay_minutes: Number(delayDraft) })}
                  className="rounded-full border border-cyan-300 bg-white px-3 py-1.5 text-[12px] font-bold text-cyan-700 hover:bg-cyan-50">
                  Save delay
                </button>
                <button data-testid="admin-thc-auto-send-run"
                  onClick={() => act(() => api.post("/admin/tank-water-health-check/report-auto-send/run"),
                    "Auto-send sweep run")}
                  className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-bold text-slate-600 hover:bg-slate-50">
                  Run sweep now
                </button>
              </div>
            </div>
          </div>
        )}

        {sms && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4"
            data-testid="admin-thc-internal-sms-panel">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[12px] font-extrabold uppercase tracking-wide text-slate-700">
                  Office SMS alerts
                </div>
                <p className="mt-1 max-w-2xl text-[12px] text-slate-600">
                  A text goes out the moment a genuine Health Check is booked, rescheduled
                  or cancelled. Numbers live here only — never in the app code.
                  {!sms.sms_configured && " SMS is not configured on this environment."}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {Object.entries(sms.events).map(([key, ev]) => (
                  <button key={key} data-testid={`admin-thc-sms-event-${key}`}
                    onClick={() => saveSms({ events: { [key]: { enabled: !ev.enabled } } })}
                    className={`rounded-full px-3 py-1.5 text-[11px] font-bold border ${ev.enabled
                      ? "bg-slate-800 text-white border-slate-800"
                      : "bg-white text-slate-500 border-slate-300"}`}>
                    {ev.label}: {ev.enabled ? "ON" : "OFF"}
                  </button>
                ))}
                <button data-testid="admin-thc-sms-test-bookings"
                  onClick={() => saveSms({ send_for_test_bookings: !sms.send_for_test_bookings })}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-bold border ${sms.send_for_test_bookings
                    ? "bg-amber-600 text-white border-amber-600"
                    : "bg-white text-slate-500 border-slate-300"}`}>
                  Test bookings: {sms.send_for_test_bookings ? "ALERTING" : "silent"}
                </button>
              </div>
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {sms.recipients.map((r) => (
                <div key={r.key} className="flex min-w-0 flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-2"
                  data-testid={`admin-thc-sms-recipient-${r.key}`}>
                  <span className="w-16 text-[12px] font-bold text-slate-700">{r.name}</span>
                  <input data-testid={`admin-thc-sms-mobile-${r.key}`} value={smsDraft[r.key] ?? ""}
                    onChange={(e) => setSmsDraft({ ...smsDraft, [r.key]: e.target.value })}
                    placeholder="04xx xxx xxx"
                    className="min-w-0 flex-1 basis-24 rounded-lg border border-slate-300 px-2 py-1 text-[12px]" />
                  <button data-testid={`admin-thc-sms-toggle-${r.key}`}
                    onClick={() => saveSms({
                      recipients: sms.recipients.map((x) => ({
                        key: x.key, name: x.name, mobile: smsDraft[x.key] ?? x.mobile,
                        enabled: x.key === r.key ? !x.enabled : x.enabled,
                      })),
                    })}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-bold border ${r.enabled
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-slate-500 border-slate-300"}`}>
                    {r.enabled ? "ON" : "OFF"}
                  </button>
                  {!r.deliverable && r.enabled && (
                    <span className="text-[11px] font-bold text-amber-700">no number</span>
                  )}
                </div>
              ))}
            </div>
            <button data-testid="admin-thc-sms-save"
              onClick={saveSmsRecipients}
              className="mt-3 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-bold text-slate-700 hover:bg-slate-50">
              Save numbers
            </button>
          </div>
        )}

        <StaffSmsPausePanel />
        <AvailabilityHealthPanel />
        <RescheduleNoticeHoldPanel />

        {queue?.total > 0 && (
          <div className="mt-6 rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="admin-thc-alternate-queue">
            <div className="flex items-center gap-2 text-[12px] font-extrabold text-amber-800">
              <AlertTriangle className="w-4 h-4" /> ALTERNATE SAMPLE COLLECTION NEEDED ({queue.total})
            </div>
            <div className="mt-2 space-y-1">
              {queue.orders.map((o) => (
                <div key={o.reference} className="text-[12px] text-amber-900" data-testid={`admin-thc-alt-${o.reference}`}>
                  <b>{o.reference}</b> · {o.test_name} · ${o.total} · paid test, Health Check {o.health_check_booking_id} cancelled
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-2"
          data-testid="admin-thc-search-bar">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input data-testid="admin-thc-search" value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, reference, address, suburb, mobile, email or technician"
              className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-9 text-[13px]" />
            {q && (
              <button data-testid="admin-thc-search-clear" onClick={() => setQ("")}
                className="absolute right-3 top-2 text-slate-400 hover:text-slate-700">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {[["upcoming", "Upcoming"], ["week", "Next 7 days"], ["past", "Past"],
            ["all", "All dates"]].map(([k, label]) => (
            <button key={k} data-testid={`admin-thc-scope-${k}`} onClick={() => setScope(k)}
              className={`rounded-full px-3 py-1.5 text-[12px] font-bold border ${scope === k
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-thc-filters">
          {[["all", "All"], ["incomplete", "Incomplete"], ["completed", "Completed"],
            ["follow_up", "Follow-up required"], ["lab", "Lab test purchased"],
            ["to_collect", "Sample to collect"], ["collected", "Sample collected"],
            ["no_action", "No action required"]].map(([k, label]) => (
            <button key={k} data-testid={`admin-thc-filter-${k}`} onClick={() => setFilter(k)}
              className={`rounded-full px-3 py-1.5 text-[12px] font-bold border ${filter === k
                ? "bg-cyan-700 text-white border-cyan-700" : "bg-white text-slate-600 border-slate-200"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-4 text-[12px] font-semibold text-slate-500"
          data-testid="admin-thc-result-count">
          Showing {shown.length} of {visible.length}
          {visible.length !== (rows || []).length
            ? ` · ${(rows || []).length} Health Checks on file` : ""}
        </div>

        <div className="mt-3 space-y-3" data-testid="admin-thc-list">
          {shown.map((b) => {
            const hasTest = b.lab_offer_status === "ordered" && !b.lab_order_cancelled_at;
            return (
              <div key={b.reference} data-testid={`admin-thc-row-${b.reference}`}
                className="rounded-xl bg-white border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-bold text-slate-900">
                      {b.first_name} {b.last_name}
                      <span className="ml-2 text-[11px] font-bold text-slate-400">{b.reference}</span>
                      {b.is_test && <span className="ml-2 text-[10px] font-bold text-amber-600">TEST</span>}
                      {b.condition_score != null && (
                        <span data-testid={`admin-thc-score-${b.reference}`}
                          title={b.condition_band || ""}
                          className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-extrabold ${
                            b.condition_score >= 85 ? "bg-emerald-100 text-emerald-800"
                              : b.condition_score >= 70 ? "bg-lime-100 text-lime-800"
                                : b.condition_score >= 50 ? "bg-amber-100 text-amber-800"
                                  : "bg-rose-100 text-rose-800"}`}>
                          {b.condition_score}/100
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-slate-500">{b.property_address}</div>
                    <div className="text-[12px] text-slate-500 mt-0.5 flex items-center gap-1">
                      <CalendarDays className="w-3.5 h-3.5" />
                      {b.appointment_date_display} · {b.appointment_time_display}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Pill testid={`admin-thc-status-${b.reference}`}
                      tone={b.status === "cancelled" ? "bg-slate-100 text-slate-500 border-slate-200"
                        : "bg-cyan-100 text-cyan-700 border-cyan-200"}>
                      {(b.status || "").toUpperCase()}
                    </Pill>
                    <Pill testid={`admin-thc-route-${b.reference}`}
                      tone={b.needs_window_notice
                        ? "bg-amber-100 text-amber-800 border-amber-300"
                        : b.route_state === "customer_notified"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-slate-600 border-slate-200"}>
                      {b.needs_window_notice
                        ? `WINDOW CHANGED · ${b.customer_window}`
                        : b.route_state === "customer_notified"
                          ? `WINDOW SENT · ${b.customer_window}`
                          : `DRAFT ROUTE · ${b.customer_window}`}
                    </Pill>
                    <Pill testid={`admin-thc-lab-${b.reference}`}
                      tone={hasTest ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-600 border-slate-200"}>
                      {hasTest ? `LAB TEST ADDED · ${b.lab_order_test_name || ""}` : "NO LAB TEST"}
                    </Pill>
                    {b.sample_collection_status && (
                      <Pill testid={`admin-thc-collection-${b.reference}`}
                        tone={COLLECTION_TONE[b.sample_collection_status] || COLLECTION_TONE.cancelled}>
                        SAMPLE {String(b.sample_collection_status).toUpperCase().replace("_", " ")}
                      </Pill>
                    )}
                    {b.lab_order_cancelled_at && (
                      <Pill testid={`admin-thc-lab-cancelled-${b.reference}`}>LAB TEST CANCELLED</Pill>
                    )}
                    {b.status === "scheduled" && !b.assigned_technician_id && (
                      <Pill testid={`admin-thc-no-tech-${b.reference}`}
                        tone="bg-amber-100 text-amber-800 border-amber-300">
                        NO TECH ASSIGNED
                      </Pill>
                    )}
                    {b.reschedule_source === "servicem8" && !b.sm8_conflict && (
                      <Pill testid={`admin-thc-sm8-inbound-${b.reference}`}
                        tone="bg-blue-100 text-blue-800 border-blue-200">
                        CHANGED IN SERVICEM8
                      </Pill>
                    )}
                    {b.technician_review_required && (
                      <Pill testid={`admin-thc-tech-review-${b.reference}`}
                        tone="bg-amber-100 text-amber-900 border-amber-200">
                        SERVICEM8 TECH UNMAPPED
                      </Pill>
                    )}
                    {b.sm8_extra_activities?.length > 0 && (
                      <Pill testid={`admin-thc-extra-staff-${b.reference}`}
                        tone="bg-amber-100 text-amber-900 border-amber-200">
                        EXTRA SERVICEM8 STAFF ({b.sm8_extra_activities.length})
                      </Pill>
                    )}
                    {b.allow_multiple_technicians && (
                      <Pill testid={`admin-thc-multi-tech-${b.reference}`}
                        tone="bg-slate-100 text-slate-700 border-slate-200">
                        MULTIPLE TECHS ALLOWED
                      </Pill>
                    )}
                    {b.sm8_conflict && (
                      <Pill testid={`admin-thc-sm8-conflict-${b.reference}`}
                        tone="bg-rose-100 text-rose-800 border-rose-200">
                        SERVICEM8 CONFLICT — CHECK TIME
                      </Pill>
                    )}
                    {b.servicem8_client_match_status === "review_required" && (
                      <Pill testid={`admin-thc-client-review-${b.reference}`}
                        tone="bg-rose-100 text-rose-800 border-rose-200">
                        SERVICEM8 CLIENT MATCH — NEEDS REVIEW
                      </Pill>
                    )}
                  </div>
                  {(b.servicem8_client_match_status === "matched_existing"
                    || b.servicem8_client_match_status === "created_new") && (
                    <div className="mt-1 text-[11px] text-slate-500"
                      data-testid={`admin-thc-client-match-status-${b.reference}`}>
                      ServiceM8 client: {b.servicem8_client_match_status === "matched_existing"
                        ? "matched existing client" : "new client created"}
                      {b.servicem8_client_match_reason
                        ? ` (${b.servicem8_client_match_reason.replaceAll("_", " ")})` : ""}
                    </div>
                  )}
                </div>

                {b.servicem8_client_match_status === "review_required" && (
                  <div className="mt-2 rounded-lg border p-2.5 text-[12px] bg-rose-50 border-rose-200 text-rose-900"
                    data-testid={`admin-thc-client-review-detail-${b.reference}`}>
                    <b>ServiceM8 client match needs review:</b>{" "}
                    {(b.servicem8_client_match_reason || "").replaceAll("_", " ")}.
                    The booking slot is held and no ServiceM8 client or job has been
                    created yet. Resolve this in{" "}
                    <b>Settings → ServiceM8 Scheduling → Sync monitor</b>.
                  </div>
                )}

                {(b.sm8_conflict || b.technician_review_required
                  || b.sm8_extra_activities?.length > 0) && (
                  <div className="mt-2 rounded-lg border p-2.5 text-[12px] bg-rose-50 border-rose-200 text-rose-900"
                    data-testid={`admin-thc-sm8-conflict-detail-${b.reference}`}>
                    {b.sm8_conflict && (
                      <div>
                        <b>ServiceM8 conflict:</b> AWARE has{" "}
                        {b.sm8_conflict.aware?.date} {b.sm8_conflict.aware?.start_time},
                        ServiceM8 has {b.sm8_conflict.servicem8?.date}{" "}
                        {b.sm8_conflict.servicem8?.start_time}. Both sides changed at
                        once — reschedule here to set the winning time.
                      </div>
                    )}
                    {b.technician_review_required && (
                      <div className={b.sm8_conflict ? "mt-1" : ""}
                        data-testid={`admin-thc-tech-review-detail-${b.reference}`}>
                        <b>Technician needs review:</b> the job was assigned in ServiceM8
                        to a staff member with no AWARE technician record. The time change
                        was applied; the AWARE technician is unchanged
                        {b.assigned_technician_name ? ` (${b.assigned_technician_name})` : ""}.
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button data-testid={`admin-thc-tech-review-dismiss-${b.reference}`}
                            disabled={busy === b.reference}
                            onClick={() => { setBusy(b.reference); dismissTechReview(b); }}
                            className="rounded-full bg-slate-800 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-slate-900">
                            <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                            Dismiss — {b.assigned_technician_name || "AWARE tech"} is correct
                          </button>
                          <button data-testid={`admin-thc-tech-review-link-${b.reference}`}
                            onClick={() => (techReview?.reference === b.reference
                              ? setTechReview(null) : openTechReview(b))}
                            className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50">
                            <UserCog className="mr-1 inline h-3.5 w-3.5" />
                            Link this ServiceM8 staff member
                          </button>
                        </div>
                        {techReview?.reference === b.reference && (
                          <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2.5 text-slate-800"
                            data-testid={`admin-thc-tech-review-panel-${b.reference}`}>
                            <div className="text-[12px]">
                              <b>ServiceM8 has:</b>{" "}
                              {techReview.sm8_staff_name
                                || (techReview.sm8_read_error
                                  ? `could not read ServiceM8 (${techReview.sm8_read_error})`
                                  : techReview.sm8_staff_uuid
                                    ? "a staff member who is no longer active in ServiceM8"
                                    : "no staff member recorded")}
                              {techReview.sm8_staff_email ? ` · ${techReview.sm8_staff_email}` : ""}
                            </div>
                            <div className="text-[12px]">
                              <b>AWARE has:</b> {techReview.aware_technician_name || "nobody assigned"}
                            </div>
                            {techReview.already_mapped_to && (
                              <div className="mt-1 text-[11px] text-slate-500">
                                Already mapped to {techReview.already_mapped_to} — re-link to change it.
                              </div>
                            )}
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <select data-testid={`admin-thc-tech-review-select-${b.reference}`}
                                value={techReview.technician_id}
                                onChange={(e) => setTechReview({ ...techReview, technician_id: e.target.value })}
                                className="rounded-lg border border-slate-300 px-2 py-1 text-[12px]">
                                <option value="">Link to AWARE technician…</option>
                                {(techReview.technicians || []).map((t) => (
                                  <option key={t.id} value={t.id}>{t.name || t.email}</option>
                                ))}
                              </select>
                              <button data-testid={`admin-thc-tech-review-save-${b.reference}`}
                                disabled={!techReview.technician_id || busy === b.reference
                                  || !techReview.sm8_staff_uuid}
                                onClick={() => { setBusy(b.reference); mapTechReview(b); }}
                                className="rounded-full bg-cyan-700 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-cyan-800 disabled:opacity-50">
                                Save link & assign
                              </button>
                            </div>
                            <p className="mt-1 text-[11px] text-slate-500">
                              Saves the ServiceM8 ↔ AWARE staff mapping, assigns that
                              technician here and pushes the dispatch back to ServiceM8.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    {b.sm8_extra_activities?.length > 0 && (
                      <div className={b.sm8_conflict || b.technician_review_required ? "mt-1" : ""}
                        data-testid={`admin-thc-extra-staff-detail-${b.reference}`}>
                        <b>Extra ServiceM8 staff on this job:</b>{" "}
                        {b.sm8_extra_activities.map(staffLabel).join(", ")}.
                        Someone scheduled them in ServiceM8.
                        {b.sm8_extra_staff_checked_at
                          && ` Last checked ${new Date(b.sm8_extra_staff_checked_at).toLocaleString("en-AU")}.`}
                        {" "}Clean it up here, or re-check in case the duplicate
                        is already gone and this warning is stale.
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button data-testid={`admin-thc-cleanup-sm8-${b.reference}`}
                            disabled={busy === b.reference || !b.appointment_id}
                            onClick={() => { setBusy(b.reference); cleanupSm8(b); }}
                            className="text-[11px] font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-full px-3 py-1.5">
                            <Users className="w-3.5 h-3.5 inline mr-1" />
                            Remove duplicate in ServiceM8
                          </button>
                          <button data-testid={`admin-thc-recheck-sm8-${b.reference}`}
                            disabled={busy === b.reference || !b.appointment_id}
                            onClick={() => { setBusy(b.reference); recheckSm8(b); }}
                            className="text-[11px] font-bold text-slate-700 border border-slate-300 bg-white hover:bg-slate-50 rounded-full px-3 py-1.5">
                            <RefreshCw className="w-3.5 h-3.5 inline mr-1" />
                            Re-check ServiceM8
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3 grid md:grid-cols-2 gap-3 text-[12px] text-slate-600">
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1">
                    <div><b>Campaign:</b> {b.campaign_source || b.source} · {b.lab_bonus_percent}% bonus</div>
                    <div data-testid={`admin-thc-lab-ref-${b.reference}`}>
                      <b>Lab order:</b> {b.lab_order_reference || "—"}
                      {b.lab_order_paid_amount ? ` · $${b.lab_order_paid_amount}` : ""}
                      {b.lab_order_paid_at ? ` · paid ${fmtAESTDate(b.lab_order_paid_at)}` : ""}
                    </div>
                    <div><b>Collection method:</b> {b.sample_collection_method || "—"}</div>
                    <div data-testid={`admin-thc-collected-by-${b.reference}`}>
                      <b>Collected:</b> {b.sample_collected_at
                        ? `${fmtAEST(b.sample_collected_at)} by ${b.sample_collected_by}`
                        : "not yet"}
                    </div>
                    {b.superseded_reason && <div><b>Superseded reason:</b> {b.superseded_reason}</div>}
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1">
                    <div><b>Confirmation email:</b> {b.confirmation_email_status || "—"}
                      {b.confirmation_email_lab_state ? ` (${b.confirmation_email_lab_state})` : ""}</div>
                    <div><b>Reminder email:</b> {b.reminder_email_status || "—"}
                      {b.reminder_email_lab_state ? ` (${b.reminder_email_lab_state})` : ""}</div>
                    {b.cancelled_at && <div><b>Cancelled:</b> {fmtAESTDate(b.cancelled_at)} · {b.cancellation_reason || "no reason given"}</div>}
                    {b.rescheduled_at && <div><b>Rescheduled:</b> {fmtAESTDate(b.rescheduled_at)}</div>}
                    <div className="flex items-center gap-2 pt-1">
                      <UserCog className="w-3.5 h-3.5 text-slate-400" />
                      <select data-testid={`admin-thc-assign-${b.reference}`}
                        value={b.assigned_technician_id || ""}
                        disabled={b.status === "cancelled"}
                        onChange={(e) => act(() => api.put(
                          `/admin/tank-water-health-check/bookings/${b.reference}/assign`,
                          { technician_id: e.target.value || null }), "Technician updated")}
                        className="flex-1 text-[12px] border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
                        <option value="">UNASSIGNED</option>
                        {techs.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name || t.email}{t.has_pin ? "" : " · NO FIELD PIN"}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 pt-1">
                      {/* Jun 2026 — office roles bypass the technician guard,
                          so the office needs a real answer, not their own click. */}
                      <button data-testid={`admin-thc-access-check-${b.reference}`}
                        disabled={busy === b.reference}
                        onClick={() => { setBusy(b.reference); checkAccess(b); }}
                        className="text-[11px] font-bold text-cyan-800 underline hover:text-cyan-950">
                        Can the tech open the link?
                      </button>
                      {/* Jun 2026 — recovery if a job has lost its ServiceM8
                          scheduling (e.g. an over-eager duplicate cleanup). */}
                      {b.status === "scheduled" && b.appointment_id && (
                        <button data-testid={`admin-thc-repush-sm8-${b.reference}`}
                          disabled={busy === b.reference}
                          onClick={() => { setBusy(b.reference); repushSm8(b); }}
                          className="text-[11px] font-bold text-slate-500 underline hover:text-slate-900">
                          Missing from ServiceM8? Push this schedule back
                        </button>
                      )}
                    </div>
                    {access && access.reference === b.reference && (
                      <div data-testid={`admin-thc-access-result-${b.reference}`}
                        className={`mt-1.5 rounded-lg border px-2 py-1.5 text-[11px] ${access.can_open
                          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                          : "border-rose-200 bg-rose-50 text-rose-900"}`}>
                        <div className="flex items-center justify-between gap-2">
                          <b>
                            {access.can_open
                              ? `${access.technician_name} can open this link on their phone`
                              : `${access.technician_name || "This technician"} cannot open this link yet`}
                          </b>
                          <button onClick={() => setAccess(null)}
                            className="text-[10px] font-bold underline">hide</button>
                        </div>
                        <ul className="mt-1 space-y-0.5">
                          {access.checks.map((c) => (
                            <li key={c.name} className="flex items-start gap-1.5">
                              {c.ok
                                ? <CheckCircle2 className="mt-[1px] h-3 w-3 shrink-0 text-emerald-600" />
                                : <XCircle className="mt-[1px] h-3 w-3 shrink-0 text-rose-600" />}
                              <span>
                                <b>{c.name}</b> — {c.detail}
                                {!c.ok && c.fix && <i className="block opacity-80">Fix: {c.fix}</i>}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* Jun 2026 — a tech with no PIN cannot open the job link
                        we text them; surface it at assignment time. */}
                    {b.assigned_technician_id
                      && techs.some((t) => t.id === b.assigned_technician_id && !t.has_pin) && (
                      <div data-testid={`admin-thc-tech-no-pin-${b.reference}`}
                        className="mt-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2 py-1.5 text-[11px] text-amber-900">
                        <b>This technician has no field PIN</b> — they can&apos;t open the job
                        link from ServiceM8.{" "}
                        <Link to="/field/technicians" className="font-bold underline">
                          Set their PIN
                        </Link>
                      </div>
                    )}
                  </div>
                </div>

                {b.status !== "cancelled" && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <select data-testid={`admin-thc-reschedule-${b.reference}`}
                      defaultValue=""
                      onChange={(e) => {
                        const s = slots.find((x) => `${x.id}|${x.start_time}` === e.target.value);
                        if (!s) return;
                        setBusy(b.reference);
                        act(() => api.post(
                          `/admin/tank-water-health-check/bookings/${b.reference}/reschedule`,
                          { slot_id: s.id, start_time: s.start_time, end_time: s.end_time }),
                        "Rescheduled — updated confirmation sent");
                      }}
                      className="text-[12px] border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
                      <option value="">Reschedule to…</option>
                      {slots.map((s) => (
                        <option key={`${s.id}|${s.start_time}`} value={`${s.id}|${s.start_time}`}>
                          {`${s.date_display} · ${s.start_display}`}
                        </option>
                      ))}
                    </select>
                    {/* Jun 2026 — move a visit to ANY date/time from AWARE
                        (the slot list only offers open availability). AWARE
                        writes first, then pushes to ServiceM8. */}
                    <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1">
                      <span className="text-[11px] font-bold text-slate-500">Move to</span>
                      <input type="date" data-testid={`admin-thc-move-date-${b.reference}`}
                        value={moveTo[b.reference]?.date || ""}
                        onChange={(e) => setMoveTo({
                          ...moveTo,
                          [b.reference]: { ...moveTo[b.reference], date: e.target.value },
                        })}
                        className="text-[12px] outline-none" />
                      <input type="time" step="900" data-testid={`admin-thc-move-time-${b.reference}`}
                        value={moveTo[b.reference]?.time || ""}
                        onChange={(e) => setMoveTo({
                          ...moveTo,
                          [b.reference]: { ...moveTo[b.reference], time: e.target.value },
                        })}
                        className="text-[12px] outline-none" />
                      <button data-testid={`admin-thc-move-go-${b.reference}`}
                        disabled={busy === b.reference
                          || !(moveTo[b.reference]?.date && moveTo[b.reference]?.time)}
                        onClick={() => { setBusy(b.reference); moveBooking(b); }}
                        className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-white disabled:opacity-40">
                        Move
                      </button>
                    </div>
                    {hasTest && (
                      <button data-testid={`admin-thc-cancel-lab-${b.reference}`}
                        onClick={() => act(() => api.post(
                          `/admin/tank-water-health-check/bookings/${b.reference}/cancel-lab-test`,
                          { reason: "admin cancelled the lab test" }),
                        "Lab test cancelled — payment untouched")}
                        className="text-[12px] font-bold text-amber-700 border border-amber-300 rounded-full px-3 py-1.5 bg-white hover:bg-amber-50">
                        <Beaker className="w-3.5 h-3.5 inline mr-1" />Cancel lab test
                      </button>
                    )}
                    <button data-testid={`admin-thc-cancel-${b.reference}`}
                      disabled={busy === b.reference}
                      onClick={() => act(() => api.post(
                        `/admin/tank-water-health-check/bookings/${b.reference}/cancel`,
                        { reason: "admin cancelled the Health Check" }),
                      "Health Check cancelled")}
                      className="text-[12px] font-bold text-rose-700 border border-rose-300 rounded-full px-3 py-1.5 bg-white hover:bg-rose-50">
                      <XCircle className="w-3.5 h-3.5 inline mr-1" />Cancel Health Check
                    </button>
                    {b.appointment_id && (
                      <button data-testid={`admin-thc-toggle-multi-tech-${b.reference}`}
                        disabled={busy === b.reference}
                        onClick={() => act(() => api.post(
                          `/admin/integrations/servicem8/appointments/${b.appointment_id}/allow-multiple-technicians`,
                          { enabled: !b.allow_multiple_technicians }),
                        b.allow_multiple_technicians
                          ? "Back to one technician per job"
                          : "Multiple technicians allowed on this job")}
                        className="text-[12px] font-bold text-slate-700 border border-slate-300 rounded-full px-3 py-1.5 bg-white hover:bg-slate-50">
                        <Users className="w-3.5 h-3.5 inline mr-1" />
                        {b.allow_multiple_technicians
                          ? "One tech only" : "Allow multiple techs"}
                      </button>
                    )}
                    {b.needs_window_notice && (
                      <button data-testid={`admin-thc-notify-window-${b.reference}`}
                        onClick={() => act(() => api.post(
                          `/admin/tank-water-health-check/bookings/${b.reference}/notify-window`),
                          "Customer sent their updated arrival window")}
                        title={`Customer was told ${b.customer_notified_window || "—"} · now ${b.customer_window}`}
                        className="rounded-full bg-amber-500 px-3 py-1.5 text-[12px] font-extrabold text-white hover:bg-amber-600">
                        <CalendarDays className="mr-1 inline h-3.5 w-3.5" />
                        Notify customer of updated window
                      </button>
                    )}
                    {b.status === "attended" && b.field_form_status !== "completed" && (
                      <button data-testid={`admin-thc-not-attended-${b.reference}`}
                        onClick={() => act(() => api.post(
                          `/admin/tank-water-health-check/bookings/${b.reference}/not-attended`),
                          "Attendance cleared — back to scheduled")}
                        title="The field form marks attendance on the first tap. Use this if the visit has not happened."
                        className="rounded-full border border-amber-300 bg-white px-3 py-1.5 text-[12px] font-bold text-amber-700 hover:bg-amber-50">
                        <XCircle className="mr-1 inline h-3.5 w-3.5" />
                        Not attended
                      </button>
                    )}
                    <button data-testid={`admin-thc-open-hc-${b.reference}`}
                      onClick={() => openDetail(b.reference)}
                      title="Onsite findings, photos, and full report history (edit, reissue, revoke)"
                      className="text-[12px] font-bold text-cyan-700 border border-cyan-300 rounded-full px-3 py-1.5 bg-white hover:bg-cyan-50">
                      <ClipboardList className="w-3.5 h-3.5 inline mr-1" />
                      {detail?.header?.reference === b.reference ? "Hide findings & report history" : "Findings & report history"}
                    </button>
                    {b.report_token && (
                      <div className="inline-flex flex-wrap items-center gap-1.5">
                        <a data-testid={`admin-thc-report-pdf-${b.reference}`}
                          href={`${API_BASE}/public/health-check-report/${b.report_token}`}
                          target="_blank" rel="noreferrer"
                          title={b.report_sent_at
                            ? `Sent to the customer ${fmtAEST(b.report_sent_at)}`
                            : "Click to open/preview the PDF — this does not send it to the customer"}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-bold text-slate-700 hover:bg-slate-50">
                          <FileText className="mr-1 inline h-3.5 w-3.5" />
                          Report v{b.report_version} · View PDF
                          {b.report_sent_at
                            ? <CheckCircle2 className="ml-1 inline h-3.5 w-3.5 text-emerald-600" />
                            : <span className="ml-1 text-[10px] font-extrabold text-amber-600">
                              NOT SENT</span>}
                        </a>
                        {!b.report_sent_at && (
                          <>
                            <Pill testid={`admin-thc-card-autosend-${b.reference}`}
                              tone={b.report_auto_send_status === "cancelled_manual_send"
                                ? manualSendTone(b.report_email_status)
                                : (AUTO_SEND_TONE[b.report_auto_send_status]
                                  || "bg-slate-100 text-slate-600 border-slate-200")}>
                              {b.report_auto_send_status === "cancelled_manual_send"
                                ? manualSendLabel(b.report_email_status,
                                    isUncertainExpired(b.report_email_status, b.report_send_uncertain_at))
                                : (AUTO_SEND_LABEL[b.report_auto_send_status] || "Awaiting manual send")}
                              {b.report_auto_send_status === "scheduled" && b.report_auto_send_at
                                ? ` · due ${fmtAEST(b.report_auto_send_at)}`
                                : ""}
                            </Pill>
                            {(b.report_auto_send_error
                              || (b.report_auto_send_status === "cancelled_manual_send"
                                && b.report_send_error)) && (
                              <span className="text-[11px] font-bold text-rose-600"
                                data-testid={`admin-thc-card-autosend-error-${b.reference}`}>
                                {b.report_auto_send_error || b.report_send_error}
                              </span>
                            )}
                            {isUncertainExpired(b.report_email_status, b.report_send_uncertain_at) ? (
                              <button data-testid={`admin-thc-report-reconcile-${b.reference}`}
                                disabled={busy === b.reference}
                                onClick={() => {
                                  if (!window.confirm(
                                    "Before unlocking a retry, check the Emails log / Resend "
                                    + `dashboard for ${b.email} and confirm the report was NOT `
                                    + "actually delivered.\n\nOnly continue if you're sure it "
                                    + "was never received — click OK to confirm that.")) return;
                                  setBusy(b.reference);
                                  act(() => api.post(
                                    `/admin/tank-water-health-check/bookings/${b.reference}/reports/${b.report_version}/reconcile-uncertain-send`,
                                    {confirmed_not_delivered: true}),
                                    "Confirmed not delivered — Send Now is unlocked again");
                                }}
                                className="rounded-full bg-orange-600 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-orange-700">
                                Confirm not delivered &amp; unlock retry
                              </button>
                            ) : (
                              <button data-testid={`admin-thc-report-send-now-${b.reference}`}
                                disabled={busy === b.reference}
                                onClick={() => {
                                  if (!window.confirm(
                                    `Send this report to the customer now?\n\nEmail: ${b.email}\n`
                                    + `Property: ${b.property_address}\nVersion: v${b.report_version}\n\n`
                                    + "Any scheduled auto-send for this report will be cancelled.")) return;
                                  act(() => api.post(
                                    `/admin/tank-water-health-check/bookings/${b.reference}/reports/${b.report_version}/send`),
                                    "Report sent to the customer");
                                }}
                                className="rounded-full bg-cyan-700 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-cyan-800">
                                Send Now
                              </button>
                            )}
                          </>
                        )}
                        {b.report_sent_at && b.sm8_attachment_status
                          && b.sm8_attachment_status !== "attached" && (
                          <>
                            <Pill testid={`admin-thc-card-sm8attach-${b.reference}`}
                              tone="bg-rose-100 text-rose-700 border-rose-200">
                              ServiceM8 attachment {b.sm8_attachment_status === "skipped_no_sm8_job"
                                ? "skipped — no job" : "failed"}
                            </Pill>
                            {b.sm8_attachment_error && (
                              <span className="text-[11px] font-bold text-rose-600"
                                data-testid={`admin-thc-card-sm8attach-error-${b.reference}`}>
                                {b.sm8_attachment_error}
                              </span>
                            )}
                            <button data-testid={`admin-thc-report-retry-attachment-${b.reference}`}
                              disabled={busy === b.reference}
                              onClick={() => {
                                setBusy(b.reference);
                                act(() => api.post(
                                  `/admin/tank-water-health-check/bookings/${b.reference}/reports/${b.report_version}/retry-attachment`),
                                  "ServiceM8 attachment retried — this never resends the email");
                              }}
                              className="rounded-full border border-cyan-300 bg-white px-3 py-1.5 text-[12px] font-bold text-cyan-700 hover:bg-cyan-50">
                              Retry ServiceM8 attachment
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {detail?.header?.reference === b.reference && (
                  <div className="mt-3 rounded-lg border border-cyan-200 bg-cyan-50/50 p-3 text-[12px]"
                    data-testid={`admin-thc-detail-${b.reference}`}>
                    <div className="font-extrabold text-slate-800">
                      ONSITE HEALTH CHECK · {(detail.form?.status || "not started").toUpperCase()}
                      {detail.form?.summary?.priority ? ` · PRIORITY ${detail.form.summary.priority.toUpperCase()}` : ""}
                    </div>
                    {detail.form?.summary ? (
                      <>
                        <ul className="mt-2 list-disc pl-5 text-slate-700" data-testid="admin-thc-detail-findings">
                          {(detail.form.summary.onsite_findings || []).map((f) => <li key={f}>{f}</li>)}
                        </ul>
                        <div className="mt-2" data-testid="admin-thc-detail-recommendations">
                          <b>Recommendations:</b> {(detail.form.summary.recommendations || []).join(" · ") || "—"}
                        </div>
                        <div className="mt-1"><b>Technician summary:</b> {detail.form.summary.technician_summary || "—"}</div>
                        <div className="mt-1">
                          <b>Owner present:</b> {detail.form.summary.owner_present || "—"} ·{" "}
                          <b>Findings shown:</b> {detail.form.summary.owner_shown_findings ? "yes" : "no"}
                        </div>
                        <div className="mt-1">
                          <b>Laboratory:</b> {detail.form.summary.lab_test_purchased
                            ? `${detail.form.summary.lab_test_name} · ${String(detail.form.summary.laboratory_stage).replace(/_/g, " ")}`
                            : "no laboratory test purchased"}
                        </div>
                      </>
                    ) : (
                      <div className="mt-2 text-slate-600">
                        Sections recorded: {Object.keys(detail.form?.sections || {}).join(", ") || "none yet"}
                      </div>
                    )}
                    {!!(detail.photos || []).length && (
                      <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-thc-detail-photos">
                        {detail.photos.map((p) => (
                          <AuthImage key={p.id} alt={p.category}
                            path={`/field/health-checks/${b.reference}/photos/${p.id}`}
                            testId={`admin-thc-detail-photo-${p.id}`}
                            className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                        ))}
                      </div>
                    )}
                    {!!(detail.follow_ups || []).length && (
                      <div className="mt-3" data-testid="admin-thc-detail-followups">
                        <b>Follow-up actions:</b>
                        {detail.follow_ups.map((f) => (
                          <div key={f.id} className="text-slate-700">
                            · {f.action_label} — {f.status.toUpperCase()}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-2"
                      data-testid={`admin-thc-reports-${b.reference}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <b className="text-slate-800">Customer report</b>
                        {detail.form?.status === "completed" && (
                          <button data-testid={`admin-thc-report-reissue-${b.reference}`}
                            onClick={() => act(() => api.post(
                              `/admin/tank-water-health-check/bookings/${b.reference}/reports/reissue`,
                              { send: false }), "New report version generated (not sent)")
                              .then(() => openDetail(b.reference, true))}
                            className="rounded-full border border-slate-300 px-3 py-1 text-[11px] font-bold text-slate-700 hover:bg-slate-50">
                            Reissue (new version)
                          </button>
                        )}
                      </div>
                      {!(detail.reports || []).length && (
                        <div className="mt-1 text-slate-500">No report generated yet.</div>
                      )}
                      {(detail.reports || []).map((r) => (
                        <div key={r.version} data-testid={`admin-thc-report-v${r.version}-${b.reference}`}
                          className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2">
                          <div className="text-slate-600">
                            <b className="text-slate-800">v{r.version}</b>
                            {r.revoked_at ? " · REVOKED" : ""} · generated{" "}
                            {fmtAEST(r.generated_at)}
                            {r.sent_at
                              ? ` · sent ${fmtAEST(r.sent_at)} to ${r.sent_to}`
                              : " · not sent"}
                            {r.open_count ? ` · opened ${r.open_count}×` : ""}
                            <div className="text-slate-500">
                              Technician: {r.technician || "—"} · Recipient: {r.recipient_email || "—"}
                            </div>
                            {r.auto_send_status && (
                              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                <Pill testid={`admin-thc-report-autosend-${r.version}-${b.reference}`}
                                  tone={r.auto_send_status === "cancelled_manual_send" && !r.sent_at
                                    ? manualSendTone(r.email_status)
                                    : AUTO_SEND_TONE[r.auto_send_status]}>
                                  {r.auto_send_status === "cancelled_manual_send" && !r.sent_at
                                    ? manualSendLabel(r.email_status,
                                        isUncertainExpired(r.email_status, r.send_uncertain_at))
                                    : (AUTO_SEND_LABEL[r.auto_send_status] || r.auto_send_status)}
                                </Pill>
                                {r.auto_send_status === "scheduled" && r.auto_send_at && (
                                  <span className="text-[11px] text-slate-500">
                                    due {fmtAEST(r.auto_send_at)}
                                  </span>
                                )}
                                {(r.auto_send_error
                                  || (r.auto_send_status === "cancelled_manual_send"
                                    && !r.sent_at && r.send_error)) && (
                                  <span className="text-[11px] text-rose-600"
                                    data-testid={`admin-thc-report-autosend-error-${r.version}-${b.reference}`}>
                                    {r.auto_send_error || r.send_error}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {!r.sent_at && !r.revoked_at && (
                              <button data-testid={`admin-thc-report-edit-${r.version}-${b.reference}`}
                                onClick={() => openEditor(b.reference, r.version)}
                                className="rounded-full border border-slate-300 px-3 py-1 text-[11px] font-bold text-slate-700 hover:bg-slate-50">
                                <Pencil className="mr-1 inline h-3 w-3" />Edit before sending
                              </button>
                            )}
                            <a data-testid={`admin-thc-report-pdf-${r.version}-${b.reference}`}
                              href={`${api.defaults.baseURL}/admin/tank-water-health-check/bookings/${b.reference}/reports/${r.version}.pdf`}
                              target="_blank" rel="noreferrer"
                              className="rounded-full border border-cyan-300 px-3 py-1 text-[11px] font-bold text-cyan-700 hover:bg-cyan-50">
                              View PDF
                            </a>
                            {!r.sent_at && !r.revoked_at && (
                              <button data-testid={`admin-thc-report-send-${r.version}-${b.reference}`}
                                onClick={() => {
                                  if (!window.confirm(
                                    `Send this report to the customer now?\n\nEmail: ${r.recipient_email}\n`
                                    + `Property: ${b.property_address}\nVersion: v${r.version}\n`
                                    + `Previous sent version: ${(detail.reports || []).filter((x) => x.sent_at).map((x) => `v${x.version}`).join(", ") || "none"}\n`
                                    + `Generated: ${fmtAEST(r.generated_at)}\n\n`
                                    + "Any scheduled auto-send for this report will be cancelled.")) return;
                                  act(() => api.post(
                                    `/admin/tank-water-health-check/bookings/${b.reference}/reports/${r.version}/send`),
                                    "Report sent to the customer").then(() => openDetail(b.reference, true));
                                }}
                                className="rounded-full bg-cyan-700 px-3 py-1 text-[11px] font-bold text-white hover:bg-cyan-800">
                                SEND NOW
                              </button>
                            )}
                            {!r.sent_at && !r.revoked_at
                              && ["scheduled", "processing"].includes(r.auto_send_status) && (
                              <button data-testid={`admin-thc-report-cancel-autosend-${r.version}-${b.reference}`}
                                onClick={() => act(() => api.post(
                                  `/admin/tank-water-health-check/bookings/${b.reference}/reports/${r.version}/cancel-auto-send`),
                                  "Scheduled auto-send cancelled").then(() => openDetail(b.reference, true))}
                                className="rounded-full border border-amber-300 px-3 py-1 text-[11px] font-bold text-amber-700 hover:bg-amber-50">
                                Cancel scheduled send
                              </button>
                            )}
                            {!r.sent_at && !r.revoked_at && r.auto_send_status === "failed" && (
                              <button data-testid={`admin-thc-report-retry-send-${r.version}-${b.reference}`}
                                onClick={() => act(() => api.post(
                                  `/admin/tank-water-health-check/bookings/${b.reference}/reports/${r.version}/retry-send`),
                                  "Send re-armed for the next sweep").then(() => openDetail(b.reference, true))}
                                className="rounded-full border border-rose-300 px-3 py-1 text-[11px] font-bold text-rose-700 hover:bg-rose-50">
                                Retry send
                              </button>
                            )}
                            {!r.revoked_at && (
                              <button data-testid={`admin-thc-report-revoke-${r.version}-${b.reference}`}
                                onClick={() => act(() => api.post(
                                  `/admin/tank-water-health-check/bookings/${b.reference}/reports/${r.version}/revoke`),
                                  "Report link revoked").then(() => openDetail(b.reference, true))}
                                className="rounded-full border border-rose-300 px-3 py-1 text-[11px] font-bold text-rose-700 hover:bg-rose-50">
                                Revoke link
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {editor && editor.reference === b.reference && (
                      <div className="mt-3 rounded-xl border-2 border-cyan-300 bg-cyan-50/40 p-3"
                        data-testid={`admin-thc-report-editor-${b.reference}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <b className="text-slate-800">
                            Edit report v{editor.version} before sending
                          </b>
                          <span className="text-[11px] text-slate-500">
                            {editor.condition_score?.score != null
                              && `Condition score ${editor.condition_score.score}/100 · ${editor.condition_score.band}`}
                          </span>
                        </div>
                        <label className="mt-2 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          Overall summary (shown beside the condition clock)
                        </label>                        <textarea data-testid="admin-thc-editor-overall" rows={3}
                          value={editor.overall_summary}
                          onChange={(e) => setEditor({ ...editor, overall_summary: e.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-[12px]" />
                        <label className="mt-2 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          Technician summary
                        </label>
                        <textarea data-testid="admin-thc-editor-tech" rows={3}
                          value={editor.technician_summary}
                          onChange={(e) => setEditor({ ...editor, technician_summary: e.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-[12px]" />
                        <label className="mt-2 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          Owner questions and notes
                        </label>
                        <textarea data-testid="admin-thc-editor-owner" rows={2}
                          value={editor.owner_notes}
                          onChange={(e) => setEditor({ ...editor, owner_notes: e.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-[12px]" />
                        <label className="mt-2 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          Recommendations (one per line)
                        </label>
                        <textarea data-testid="admin-thc-editor-recs" rows={3}
                          value={(editor.recommendations || []).join("\n")}
                          onChange={(e) => setEditor({
                            ...editor, recommendations: e.target.value.split("\n"),
                          })}
                          className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-[12px]" />
                        <div className="mt-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          Photos in the report ({(editor.photos || []).filter((p) => p.included).length}
                          {" "}of {(editor.photos || []).length})
                        </div>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {(editor.photos || []).map((p) => (
                            <button key={p.id} type="button"
                              data-testid={`admin-thc-editor-photo-${p.id}`}
                              onClick={() => setEditor({
                                ...editor,
                                photos: editor.photos.map((x) => x.id === p.id
                                  ? { ...x, included: !x.included } : x),
                              })}
                              className={`rounded-lg border-2 p-0.5 ${p.included
                                ? "border-cyan-600" : "border-slate-200 opacity-50"}`}>
                              <AuthImage
                                path={`/admin/tank-water-health-check/bookings/${b.reference}/photos/${p.id}`}
                                alt={p.caption || p.category} className="h-14 w-16 rounded object-cover" />
                              <div className="text-[9px] font-bold text-slate-500">
                                {p.included ? "INCLUDED" : "HIDDEN"}
                              </div>
                            </button>
                          ))}
                          {!(editor.photos || []).length && (
                            <span className="text-[11px] text-slate-500">No photos on this job.</span>
                          )}
                        </div>
                        <ReportReviewPanel editor={editor} setEditor={setEditor}
                          onSave={() => saveEditor(b.reference)}
                          onSetStatus={(s) => saveEditor(b.reference, s)} />
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button data-testid="admin-thc-editor-save"
                            onClick={() => saveEditor(b.reference)}
                            className="rounded-full bg-cyan-700 px-4 py-1.5 text-[11px] font-bold text-white hover:bg-cyan-800">
                            Save changes
                          </button>
                          <button data-testid="admin-thc-editor-cancel"
                            onClick={() => setEditor(null)}
                            className="rounded-full border border-slate-300 px-4 py-1.5 text-[11px] font-bold text-slate-600">
                            Close
                          </button>
                          <span className="self-center text-[11px] text-slate-500">
                            Saving re-renders the PDF. Send when you&apos;re happy with it.
                          </span>
                        </div>
                      </div>
                    )}

                    <CustomerActionsPanel reference={b.reference} />

                    <details className="mt-3">
                      <summary className="cursor-pointer font-bold text-slate-600">Audit history</summary>                      <div className="mt-1 space-y-0.5" data-testid="admin-thc-detail-audit">
                        {(detail.audit || []).map((a, i) => (
                          <div key={i} className="text-slate-500">
                            {fmtAEST(a.at)} · {a.event}{a.by ? ` · ${a.by}` : ""}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!shown.length && (
          <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center"
            data-testid="admin-thc-empty">
            <p className="text-sm font-bold text-slate-700">No Health Checks match this view</p>
            <p className="mt-1 text-[12px] text-slate-500">
              {q ? "Try a different search, " : ""}or widen the date range to All dates.
            </p>
          </div>
        )}
        {shown.length < visible.length && (
          <button data-testid="admin-thc-load-more" onClick={() => setLimit((n) => n + PAGE)}
            className="mt-4 w-full rounded-xl border border-slate-300 bg-white py-3 text-[12px] font-extrabold text-slate-700 hover:bg-slate-50">
            LOAD {Math.min(PAGE, visible.length - shown.length)} MORE
            · {visible.length - shown.length} remaining
          </button>
        )}

        <div className="mt-10 rounded-xl border border-slate-200 bg-white p-4" data-testid="admin-thc-followups">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Health Check follow-up actions</h2>
              <p className="mt-0.5 text-[12px] text-slate-500">
                Created automatically from technician recommendations — one action per
                recommendation per Health Check.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select data-testid="admin-thc-fu-type" value={fuType}
                onChange={(e) => { setFuType(e.target.value); loadFollowUps(e.target.value, fuStatus); }}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[12px]">
                <option value="">All types</option>
                {Object.entries(fu?.types || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select data-testid="admin-thc-fu-status" value={fuStatus}
                onChange={(e) => { setFuStatus(e.target.value); loadFollowUps(fuType, e.target.value); }}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[12px]">
                <option value="">All statuses</option>
                {(fu?.statuses || []).map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {!(fu?.follow_ups || []).length && (
              <div className="text-[12px] text-slate-500" data-testid="admin-thc-fu-empty">
                No follow-up actions for this filter.
              </div>
            )}
            {(fu?.follow_ups || []).map((f) => (
              <div key={f.id} data-testid={`admin-thc-fu-${f.id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-3 text-[12px]">
                <div>
                  <div className="font-bold text-slate-800">{f.action_label}</div>
                  <div className="text-slate-500">
                    {f.customer_name} · {f.property_address} · {f.health_check_reference}
                    {f.is_test ? " · TEST" : ""}
                  </div>
                  {f.technician_notes && <div className="mt-0.5 text-slate-600">“{f.technician_notes}”</div>}
                </div>
                <select data-testid={`admin-thc-fu-status-${f.id}`} value={f.status}
                  onChange={(e) => act(() => api.patch(
                    `/admin/tank-water-health-check/follow-ups/${f.id}/status`,
                    { status: e.target.value }), "Follow-up updated").then(() => loadFollowUps())}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[12px] font-bold">
                  {(fu?.statuses || []).map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 rounded-xl border border-slate-200 bg-white p-4" data-testid="admin-thc-cleanup">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Test record cleanup</h2>
              <p className="text-[12px] text-slate-500 mt-0.5">
                Only ever touches records flagged <b>is_test</b>. Dry run first; anything with a paid
                lab order is refused and listed for manual review.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="admin-thc-cleanup-dryrun"
                onClick={() => api.get("/admin/tank-water-health-check/test-records/cleanup")
                  .then(({ data }) => { setCleanup(data); toast.success("Dry run complete — nothing changed"); })
                  .catch((e) => toast.error(formatApiError(e)))}
                className="text-[12px] font-bold text-slate-700 border border-slate-200 rounded-full px-3 py-1.5 bg-white hover:bg-slate-50">
                Dry run
              </button>
              <button data-testid="admin-thc-cleanup-apply"
                disabled={!cleanup?.cleanable?.length}
                onClick={() => act(async () => {
                  const { data } = await api.post("/admin/tank-water-health-check/test-records/cleanup", { confirm: true });
                  setCleanup(data);
                }, "Test records cleaned up and slots released")}
                className="text-[12px] font-bold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-40 rounded-full px-3 py-1.5">
                <Trash2 className="w-3.5 h-3.5 inline mr-1" />Clean up
              </button>
            </div>
          </div>
          {cleanup && (
            <div className="mt-3 text-[12px] space-y-2" data-testid="admin-thc-cleanup-result">
              <div className="font-bold text-slate-700">
                {cleanup.dry_run === false
                  ? `Deleted ${cleanup.deleted_health_checks?.length || 0} test Health Checks, ${cleanup.deleted_lab_orders?.length || 0} unpaid lab orders, released ${cleanup.released_slots?.length || 0} slots`
                  : `${cleanup.cleanable?.length || 0} cleanable · ${cleanup.manual_review_required?.length || 0} need manual review`}
              </div>
              {(cleanup.cleanable || []).map((c) => (
                <div key={c.reference} className="text-slate-600" data-testid={`admin-thc-cleanup-row-${c.reference}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 text-emerald-600" />
                  {c.reference} · {c.appointment_date} {c.appointment_time_display} · would delete{" "}
                  {c.would_delete.join(", ")} · would release slot {c.would_release || "—"}
                </div>
              ))}
              {(cleanup.manual_review_required || []).map((c) => (
                <div key={c.reference} className="text-amber-700" data-testid={`admin-thc-cleanup-blocked-${c.reference}`}>
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                  {c.reference} · blocked: {c.blocker} · retained: {c.would_retain.join(", ")}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PortalShell>
  );
}
